import React from 'react';
import { ArrowLeft, BookOpen, Search, Lightbulb, Layers, GraduationCap, Users, Award, Compass, Sparkles } from 'lucide-react';
import './FooterPages.css';
import './LearnBasics.css';

const buildingBlocks = [
  { term: 'Research Problem', definition: 'The issue or gap in knowledge that your study addresses. Without a clear problem, your research has no direction.' },
  { term: 'Research Question', definition: 'The specific question your study sets out to answer. It should be focused enough to be answerable but broad enough to be meaningful.' },
  { term: 'Literature Review', definition: 'The process of reading what other researchers have already written about your topic. It tells you what is already known, what is still unknown, and where your research fits in.' },
  { term: 'Methodology', definition: 'The plan you follow to collect and analyse your data. It explains how you will gather evidence and why you chose that particular approach.' },
  { term: 'Data', definition: 'The information you collect. It can be numbers, interview responses, survey answers, observations, documents, or measurements.' },
  { term: 'Analysis', definition: 'What you do with the data you collect. You organise it, examine it, and draw meaning from it.' },
  { term: 'Findings & Conclusions', definition: 'The results of your analysis and the answers you arrive at based on your evidence.' },
];

const levels = [
  {
    icon: GraduationCap,
    level: 'Undergraduate',
    subtitle: 'Research as Exploration',
    color: '#4f46e5',
    content: [
      'If you are doing research for the first time at the undergraduate level, your primary goal is to understand and demonstrate that you can engage with existing knowledge in a structured way.',
      'Your research project or dissertation is not expected to discover something that no scholar has ever found before. Instead, you are expected to show that you can identify a relevant topic, review what others have written about it, collect basic information or data, and present your findings in a logical and organised manner.',
      'At this level, you will often use a small sample size, a simple survey or questionnaire, or a basic analysis of existing documents. The most important skill to develop is clarity. You must be able to explain what your study is about, why it matters, what you did, what you found, and what it means. Write simply. Avoid jargon. Let your evidence speak for itself.',
      'The most common mistake at this level is choosing a topic that is too broad. The narrower and more focused your question, the easier it becomes to carry out your research and write about it clearly.',
    ],
  },
  {
    icon: Users,
    level: 'Masters',
    subtitle: 'Research as Application',
    color: '#0891b2',
    content: [
      'At the masters level, the expectations go deeper. You are no longer just engaging with existing knowledge. You are expected to apply that knowledge to a real problem, test a theory in a new context, or produce findings that are practically useful.',
      'Your literature review must be more thorough. You are expected to compare the views of different scholars, identify contradictions in existing studies, and position your work within an ongoing academic conversation. This is where the concept of a research gap becomes important. A research gap is a specific area where existing studies have not fully answered a question, used a particular method, or examined a particular context. Your study should aim to fill that gap.',
      'At this level, your methodology must be justified. It is not enough to say you used a questionnaire. You must explain why a questionnaire was the most suitable tool for your research question, what its limitations are, and how you ensured your data was valid and reliable.',
      'Masters research is also expected to have implications. What do your findings mean for practitioners, organisations, or policy? How can someone use what you discovered?',
    ],
  },
  {
    icon: Award,
    level: 'PhD',
    subtitle: 'Research as Original Contribution',
    color: '#7c3aed',
    content: [
      'PhD research is the most demanding level, and for good reason. A doctoral thesis is expected to make an original contribution to knowledge. This means your study must discover, prove, disprove, or develop something that did not exist in the body of knowledge before you studied it.',
      'Original contribution does not always mean an entirely new discovery. It can mean applying an existing theory to a new population, combining two frameworks in a way that no one has done before, developing a new model, or testing an established method in a completely different geographical or cultural context.',
      'At the PhD level, your research philosophy matters. You must understand whether your study takes a positivist approach, which assumes that reality is objective and measurable, or an interpretivist approach, which assumes that meaning is constructed through human experience. These philosophical positions shape every decision you make about how to design and conduct your study.',
      'Your literature review at this level must be exhaustive. Your methodology must be sophisticated and clearly defended. Your analysis must go beyond description into deep interpretation. And your conclusions must show precisely how your study has advanced knowledge in your field.',
    ],
  },
];

const wizardJourney = [
  { letter: 'A', label: 'Research Gap Finder', color: '#0d9488' },
  { letter: 'B', label: 'Research Methodology', color: '#4f46e5' },
  { letter: 'C', label: 'Data Analysis Planning', color: '#0891b2' },
  { letter: 'D', label: 'Framework Builder', color: '#7c3aed' },
  { letter: 'E', label: 'Data Collection', color: '#059669' },
  { letter: 'F', label: 'Ethics Approval', color: '#dc2626' },
  { letter: 'G', label: 'Survey Builder', color: '#0ea5e9' },
];

const LearnBasics = ({ onBack }) => {
  return (
    <div className="footer-page">
      <button className="back-button" onClick={onBack}>
        <ArrowLeft size={20} />
        Back
      </button>

      <div className="footer-page-content">

        <div className="lb-hero">
          <div className="lb-hero-icon">
            <BookOpen size={40} />
          </div>
          <h1 className="lb-hero-title">The Basics of Research</h1>
          <p className="lb-hero-subtitle">A Beginner's Guide for Every Level of Student</p>
        </div>

        <div className="lb-section">
          <div className="lb-section-header">
            <Search size={22} />
            <h2>What Is Research?</h2>
          </div>
          <p className="lb-body">Research is simply the process of asking a question and finding a trustworthy answer to it. Every day, people ask questions. A farmer wonders which crop grows best in dry soil. A nurse asks whether a new drug reduces patient recovery time. A student wants to know why some buildings collapse during earthquakes while others do not. Research is what transforms those everyday questions into structured, credible, and useful knowledge that others can rely on.</p>
          <p className="lb-body">At its heart, research is not reserved for professors or scientists in laboratories. It is a thinking skill that anyone can learn. When you conduct research, you are essentially going through three broad activities:</p>
          <div className="lb-steps">
            <div className="lb-step">
              <span className="lb-step-number">1</span>
              <p>Identify a problem or a question that needs an answer.</p>
            </div>
            <div className="lb-step">
              <span className="lb-step-number">2</span>
              <p>Gather information or evidence that helps you understand the problem better.</p>
            </div>
            <div className="lb-step">
              <span className="lb-step-number">3</span>
              <p>Analyse what you found and communicate your conclusions clearly.</p>
            </div>
          </div>
          <div className="highlight-box">
            <p>That is the foundation. Everything else in research is simply a more detailed way of doing those three things.</p>
          </div>
        </div>

        <div className="lb-section">
          <div className="lb-section-header">
            <Lightbulb size={22} />
            <h2>Why Research Matters</h2>
          </div>
          <p className="lb-body">Research matters because it moves the world forward. Every medicine you take was tested through research. Every bridge built today uses methods that researchers improved over decades. Every government policy that works well was informed by evidence that researchers gathered and analysed.</p>
          <p className="lb-body">For you as a student, research matters for a simpler reason. It teaches you to think critically, ask better questions, find reliable information, and communicate your ideas with evidence rather than guessing. These skills are useful in every career and in everyday life.</p>
        </div>

        <div className="lb-section">
          <div className="lb-section-header">
            <Layers size={22} />
            <h2>The Common Building Blocks of Research</h2>
          </div>
          <p className="lb-body">Regardless of your level of study, every research project shares the same basic building blocks. These elements appear in every research project, whether you are an undergraduate student writing a short project report or a PhD student completing a 100,000 word thesis.</p>
          <div className="lb-blocks-grid">
            {buildingBlocks.map((block, i) => (
              <div key={i} className="lb-block-card">
                <div className="lb-block-number">{i + 1}</div>
                <h4 className="lb-block-term">{block.term}</h4>
                <p className="lb-block-def">{block.definition}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lb-section">
          <div className="lb-section-header">
            <GraduationCap size={22} />
            <h2>Research at Every Level</h2>
          </div>
          <p className="lb-body">While the building blocks remain the same, the expectations and depth of research differ significantly depending on your level of study. Here is what is expected at each stage.</p>
          <div className="lb-levels">
            {levels.map((lvl) => {
              const Icon = lvl.icon;
              return (
                <div key={lvl.level} className="lb-level-card" style={{ '--level-color': lvl.color }}>
                  <div className="lb-level-header" style={{ backgroundColor: lvl.color }}>
                    <div className="lb-level-icon">
                      <Icon size={26} />
                    </div>
                    <div>
                      <h3 className="lb-level-title">For {lvl.level} Students</h3>
                      <p className="lb-level-subtitle">{lvl.subtitle}</p>
                    </div>
                  </div>
                  <div className="lb-level-body">
                    {lvl.content.map((para, i) => (
                      <p key={i} className="lb-body">{para}</p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lb-section">
          <div className="lb-section-header">
            <Compass size={22} />
            <h2>The One Thing That Connects Every Researcher</h2>
          </div>
          <div className="lb-closing-box">
            <p>Whether you are an undergraduate student writing your first project, a masters student producing a dissertation, or a doctoral researcher building a career in academia, one principle connects you all.</p>
            <p className="lb-closing-quote">"Good research starts with genuine curiosity. If you care about your question, the process becomes meaningful rather than mechanical. Ask questions boldly, read widely, think critically, and write honestly. That is all research ever asks of you."</p>
          </div>
        </div>

        <div className="lb-section lb-app-section">
          <div className="lb-section-header">
            <Sparkles size={22} />
            <h2>How the Research Methodology Wizard Helps You</h2>
          </div>
          <p className="lb-body">
            Whether you are an undergraduate tackling your first research project, a Masters student navigating a more complex dissertation, or a PhD candidate developing an original contribution to knowledge, the Research Methodology Wizard is designed to meet you at your level and guide you forward in a structured, step-by-step way.
          </p>
          <p className="lb-body">
            Rather than facing an overwhelming blank page, the Wizard breaks the entire research process down into seven focused tools — each one handling a specific stage of your research journey. You begin with the <strong>Research Gap Finder</strong> to identify what is missing in the existing literature, then move into the <strong>Research Methodology Wizard</strong> to choose the right philosophical approach and design for your study. The <strong>Data Analysis Planning</strong> tool helps you decide how you will analyse your data before you even collect it, so your survey questions and instruments are built around a clear analytical strategy.
          </p>
          <p className="lb-body">
            From there, the <strong>Framework Builder</strong> helps you map your key constructs and theoretical foundations into a visual conceptual framework — ready to include in your methodology chapter. The <strong>Data Collection Process</strong> Wizard walks you through every major collection method with tips and best practices, while the <strong>Ethics Approval</strong> Wizard ensures your research is conducted responsibly, whether or not your institution formally requires it. Finally, the <strong>Survey Builder and Qualitative Analysis</strong> tool lets you create your questionnaire and analyse your data in one place.
          </p>
          <p className="lb-body">
            Each Wizard is self-contained but connected — your choices in one carry forward into the next, building a coherent research design from start to finish. You can save your work, compare multiple designs side by side, generate reports, and refine your methodology as many times as you need. The result is a research process that feels manageable, intentional, and entirely yours.
          </p>
          <div className="lb-journey">
            {wizardJourney.map((w, i) => (
              <React.Fragment key={w.letter}>
                <div className="lb-journey-card" style={{ borderColor: w.color }}>
                  <span className="lb-journey-letter" style={{ color: w.color }}>Wizard {w.letter}</span>
                  <p className="lb-journey-label">{w.label}</p>
                </div>
                {i < wizardJourney.length - 1 && (
                  <span className="lb-journey-arrow">→</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default LearnBasics;
